import { useMemo, useState } from "react";
import { dramas } from "@/mocks/dramas";
import { heatRankIds, ratingRankIds, newReleases } from "@/mocks/content";
import type { Drama } from "@/pages/home/types";

type TabKey = "heat" | "rating" | "new";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "heat", label: "站内热度", icon: "ri-fire-line" },
  { key: "rating", label: "口碑评分", icon: "ri-star-smile-line" },
  { key: "new", label: "近期开播", icon: "ri-calendar-check-line" }
];

export default function RankingSection() {
  const [active, setActive] = useState<TabKey>("heat");

  const dramaMap = useMemo(() => {
    const map = new Map<string, Drama>();
    dramas.forEach((d) => map.set(d.id, d as Drama));
    return map;
  }, []);

  const list = useMemo(() => {
    if (active === "heat") {
      return heatRankIds.map((id) => dramaMap.get(id)).filter(Boolean) as Drama[];
    }
    if (active === "rating") {
      return ratingRankIds.map((id) => dramaMap.get(id)).filter(Boolean) as Drama[];
    }
    return newReleases
      .map((r) => dramaMap.get(r.id))
      .filter(Boolean) as Drama[];
  }, [active, dramaMap]);

  const badgeFor = (index: number) => {
    if (active === "new") {
      const item = newReleases.find((r) => r.id === list[index]?.id);
      return item?.badge ?? "新剧";
    }
    return null;
  };

  return (
    <section id="ranking" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-sm font-medium text-primary-600">本周焦点</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              热播排行 · 大家都在追什么
            </h2>
            <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600">
              榜单综合近期播放、收藏与讨论热度实时排序。切换标签，从最热门的当下，到最耐看的口碑，再到刚刚开播的新剧，一眼看全。
            </p>
          </div>

          <div
            role="tablist"
            aria-label="排行榜切换"
            className="inline-flex items-center gap-1 self-start rounded-full border border-background-200/70 bg-background-100 px-1 py-1"
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active === tab.key}
                onClick={() => setActive(tab.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 font-label text-sm transition-colors md:px-4 ${
                  active === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                <i className={tab.icon} aria-hidden="true"></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <ol className="mt-8 grid grid-cols-1 gap-2.5 lg:grid-cols-2">
          {list.map((drama, index) => (
            <li
              key={drama.id}
              className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100/60 md:gap-4"
            >
              <span className="w-8 shrink-0 text-center font-heading text-xl font-black text-foreground-300 md:text-2xl">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-24 md:w-16">
                <img
                  src={drama.poster}
                  alt={`${drama.title} 剧集海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                    {drama.title}
                  </h3>
                  {badgeFor(index) && (
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 font-label text-[11px] text-accent-900">
                      {badgeFor(index)}
                    </span>
                  )}
                </div>
                <p className="mt-1 font-label text-xs text-foreground-500">
                  {drama.year} · {drama.genres.slice(0, 2).join(" / ")} · {drama.cast}
                </p>
                <p className="mt-1.5 hidden line-clamp-2 font-body text-xs leading-relaxed text-foreground-600 sm:block">
                  {drama.summary}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {active === "rating" ? (
                  <>
                    <span className="block font-heading text-lg font-bold text-accent-700 md:text-xl">
                      {drama.rating.toFixed(1)}
                    </span>
                    <span className="font-label text-[11px] text-foreground-500">口碑分</span>
                  </>
                ) : (
                  <>
                    <span className="block font-heading text-lg font-bold text-primary-600 md:text-xl">
                      {(drama.heat / 1000).toFixed(1)}k
                    </span>
                    <span className="font-label text-[11px] text-foreground-500">热度</span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}